import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Folder } from "~/api/model";
import { useKnowledgeDocumentUpload } from "~/features/settings/hooks/useKnowledgeDocumentUpload";
import { useReportSampleUpload } from "~/features/settings/hooks/useReportSampleUpload";
import { ROUTES, useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useFtue } from "~/hooks/useFtue";
import {
  FtueCreateCase,
  FtueUploadKnowledge,
  FtueUploadReport,
  FtueUploadSuccess,
} from "./ftue";

enum FtueStep {
  UPLOAD_REPORT = "upload_report",
  REPORT_SUCCESS = "report_success",
  UPLOAD_KNOWLEDGE = "upload_knowledge",
  KNOWLEDGE_SUCCESS = "knowledge_success",
  CREATE_CASE = "create_case",
}

interface FtueWelcomeProps {
  demoCase?: Folder;
  isDemoCaseLoading?: boolean;
}

export const FtueWelcome: FC<FtueWelcomeProps> = ({
  demoCase,
  isDemoCaseLoading,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { user, completeFtue, isCompletingFtue, trackFtueEvent } = useFtue();
  const { toast } = useToast();
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [currentStep, setCurrentStep] = useState<FtueStep>(
    FtueStep.UPLOAD_REPORT
  );

  const { uploadReportSample, isUploading, uploadProgress } =
    useReportSampleUpload({ organizationId, setAsDefault: true });

  const {
    uploadKnowledgeDocument,
    isUploading: isUploadingKnowledge,
    uploadProgress: knowledgeUploadProgress,
  } = useKnowledgeDocumentUpload({ organizationId });

  useEffect(() => {
    if (!hasTrackedView) {
      trackFtueEvent("ftue_welcome_viewed");
      setHasTrackedView(true);
    }
  }, [hasTrackedView, trackFtueEvent]);

  const firstName = user?.name?.split(" ")[0] || "";

  const handleReportUpload = async (file: File) => {
    try {
      await uploadReportSample(file);
      setCurrentStep(FtueStep.REPORT_SUCCESS);
    } catch {
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "ftue.uploadReport.error",
          defaultMessage: "Failed to upload report",
        }),
      });
    }
  };

  const handleKnowledgeUpload = async (file: File) => {
    try {
      await uploadKnowledgeDocument(file);
      setCurrentStep(FtueStep.KNOWLEDGE_SUCCESS);
    } catch {
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "ftue.uploadKnowledge.error",
          defaultMessage: "Failed to upload document",
        }),
      });
    }
  };

  const handleExploreDemoCase = async () => {
    trackFtueEvent("ftue_explore_demo_clicked");
    await completeFtue("explore_demo");
    if (demoCase) {
      navigate(ROUTES.CASE({ organizationId, caseId: demoCase.id }));
    }
  };

  const handleSkip = async () => {
    trackFtueEvent("ftue_skipped");
    await completeFtue("skip");
  };

  const handleCreateCaseClick = () => {
    trackFtueEvent("ftue_create_case_clicked");
  };

  // Step: Upload Report
  if (currentStep === FtueStep.UPLOAD_REPORT) {
    return (
      <FtueUploadReport
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        onUpload={handleReportUpload}
        onSkip={() => setCurrentStep(FtueStep.UPLOAD_KNOWLEDGE)}
      />
    );
  }

  // Step: Report Upload Success
  if (currentStep === FtueStep.REPORT_SUCCESS) {
    return (
      <FtueUploadSuccess
        titleId="ftue.reportSuccess.title"
        titleDefault="Report Uploaded Successfully!"
        subtitleId="ftue.reportSuccess.subtitle"
        subtitleDefault="Sky AI will use this as a template for generating your reports."
        onContinue={() => setCurrentStep(FtueStep.UPLOAD_KNOWLEDGE)}
      />
    );
  }

  // Step: Upload Knowledge Document
  if (currentStep === FtueStep.UPLOAD_KNOWLEDGE) {
    return (
      <FtueUploadKnowledge
        isUploading={isUploadingKnowledge}
        uploadProgress={knowledgeUploadProgress}
        onUpload={handleKnowledgeUpload}
        onSkip={() => setCurrentStep(FtueStep.CREATE_CASE)}
      />
    );
  }

  // Step: Knowledge Upload Success
  if (currentStep === FtueStep.KNOWLEDGE_SUCCESS) {
    return (
      <FtueUploadSuccess
        titleId="ftue.knowledgeSuccess.title"
        titleDefault="Document Added Successfully!"
        subtitleId="ftue.knowledgeSuccess.subtitle"
        subtitleDefault="Sky AI can now reference this document when analyzing your cases."
        onContinue={() => setCurrentStep(FtueStep.CREATE_CASE)}
      />
    );
  }

  // Step: Create Case
  if (currentStep === FtueStep.CREATE_CASE) {
    return (
      <FtueCreateCase
        firstName={firstName}
        demoCase={demoCase}
        isDemoCaseLoading={isDemoCaseLoading}
        isCompletingFtue={isCompletingFtue}
        onCreateCaseClick={handleCreateCaseClick}
        onCaseCreated={() => completeFtue("create_case")}
        onExploreDemoCase={handleExploreDemoCase}
        onSkip={handleSkip}
      />
    );
  }
};
